/**
 * Emoji pack: standard Unicode emoji (rendered by the device's own emoji font — no image downloads,
 * no third-party artwork). Recently used emoji are remembered on this device only (not message content).
 */
import { useEffect, useRef, useState } from 'react';

/** Splits a string of emoji into grapheme clusters (keeps ZWJ sequences and flags together). */
function split(s: string): string[] {
  const Seg = (Intl as unknown as { Segmenter?: typeof Intl.Segmenter }).Segmenter;
  const parts = Seg
    ? [...new Seg(undefined, { granularity: 'grapheme' }).segment(s)].map((x) => x.segment)
    : (s.match(/\p{RI}\p{RI}|\p{Extended_Pictographic}(\uFE0F|\u200D\p{Extended_Pictographic}\uFE0F?)*/gu) ??
      []);
  return parts.filter((x) => x.trim());
}

export const EMOJI_GROUPS: { id: string; label: string; icon: string; emoji: string[] }[] = [
  {
    id: 'faces',
    label: 'Yüzler',
    icon: '😀',
    emoji: split(
      '😀😃😄😁😆😅😂🤣😊😇🙂🙃😉😌😍🥰😘😗😋😛😜🤪😝🤑🤗🤭🤫🤔🤐🤨😐😑😶😏😒🙄😬🤥😴😷🤒🤕🤢🤮🥵🥶🥴😵🤯🤠🥳😎🤓🧐😕😟🙁😮😯😲😳🥺😦😧😨😰😥😢😭😱😖😣😞😓😩😫🥱😤😡😠🤬😈💀💩🤡👻👽🤖😺😸😹😻😼😽🙀😿😾',
    ),
  },
  {
    id: 'hands',
    label: 'El ve insan',
    icon: '👍',
    emoji: split(
      '👍👎👌🤌🤏✌️🤞🤟🤘🤙👈👉👆👇☝️✋🤚🖐️🖖👋🤝🙏👏🙌👐🤲💪🫶✍️👀🧠🫡🙋🙆🙅🤷🤦💁🙇🏃🚶💃🕺🧑‍💻👨‍💻👩‍💻🧑‍💼👨‍💼👩‍💼👶👦👧🧑👨👩👴👵',
    ),
  },
  {
    id: 'hearts',
    label: 'Kalpler ve semboller',
    icon: '❤️',
    emoji: split(
      '❤️🧡💛💚💙💜🖤🤍🤎💔❣️💕💞💓💗💖💘💝✅❌⭕❗❓‼️⁉️💯🔥✨⭐🌟💥💢💤💬💭⚠️🚫🔴🟠🟡🟢🔵🟣⚫⚪➕➖✔️🆗🆕🆒🔝🔜♻️',
    ),
  },
  {
    id: 'work',
    label: 'İş ve nesneler',
    icon: '💼',
    emoji: split(
      '💼📁📂📄📊📈📉📌📎🖇️📝✏️🗂️📅📆⏰⏳⌛💡🔧🔨🛠️⚙️🔒🔓🔑💻🖥️⌨️🖱️📱☎️📞📧✉️📦🚀🎯🏆🥇🎉🎊🎁🔔📣💰💳🧾📷🎧🎮',
    ),
  },
  {
    id: 'nature',
    label: 'Doğa ve yiyecek',
    icon: '🌻',
    emoji: split(
      '☀️🌤️⛅🌧️⛈️❄️🌈🌙🌊🌱🌳🌸🌻🍀🍁🐶🐱🦊🐻🐼🐵🐦🦄🐝🐢🐟☕🍵🍺🍷🥂🍕🍔🍟🌯🥗🍰🎂🍫🍎🍉🍌🍓🧀🥐🍩🍿',
    ),
  },
  {
    id: 'travel',
    label: 'Yer ve bayrak',
    icon: '🚗',
    emoji: split('🇹🇷🚗🚕🚌🚚✈️🚆🚲🏠🏢🏭🏥🏫🏖️⛰️🗺️📍🧭⛽🚦🌍🏁🚩🏳️'),
  },
];

const RECENT_KEY = 'cr.emoji.recent';

function loadRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, 24) : [];
  } catch {
    return [];
  }
}

function saveRecent(list: string[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 24)));
  } catch {
    /* storage unavailable: recent list just isn't remembered */
  }
}

/** Inserts text at the caret of an input/textarea and returns the new value + caret. */
export function insertAt(el: HTMLInputElement | HTMLTextAreaElement | null, value: string, text: string) {
  const start = el?.selectionStart ?? value.length;
  const end = el?.selectionEnd ?? value.length;
  const next = value.slice(0, start) + text + value.slice(end);
  const caret = start + text.length;
  requestAnimationFrame(() => {
    if (!el) return;
    el.focus();
    el.setSelectionRange(caret, caret);
  });
  return next;
}

export function EmojiButton({
  onPick,
  label = 'Emoji ekle',
  className = 'btn small ghost',
  placement = 'up',
}: {
  onPick: (emoji: string) => void;
  label?: string;
  className?: string;
  placement?: 'up' | 'down';
}) {
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);
  const [group, setGroup] = useState('faces');
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    setRecent(loadRecent());
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (e: string) => {
    const next = [e, ...recent.filter((x) => x !== e)].slice(0, 24);
    setRecent(next);
    saveRecent(next);
    onPick(e);
  };

  const current = group === 'recent' ? recent : (EMOJI_GROUPS.find((g) => g.id === group)?.emoji ?? []);

  return (
    <span className="emoji-wrap">
      <button
        ref={btn}
        type="button"
        className={className}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        😊
      </button>
      {open && (
        <div ref={box} className={`emoji-pop ${placement}`} role="dialog" aria-label="Emoji seç">
          <div className="emoji-tabs" role="tablist" aria-label="Emoji grupları">
            {recent.length > 0 && (
              <button
                type="button"
                role="tab"
                aria-selected={group === 'recent'}
                aria-label="Son kullanılanlar"
                title="Son kullanılanlar"
                onClick={() => setGroup('recent')}
              >
                🕘
              </button>
            )}
            {EMOJI_GROUPS.map((g) => (
              <button
                key={g.id}
                type="button"
                role="tab"
                aria-selected={group === g.id}
                aria-label={g.label}
                title={g.label}
                onClick={() => setGroup(g.id)}
              >
                {g.icon}
              </button>
            ))}
          </div>
          <div className="emoji-grid" role="group" aria-label="Emojiler">
            {current.map((e) => (
              <button key={e} type="button" onClick={() => pick(e)} aria-label={e}>
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}
